import { CaretLeft, LockSimple, LockSimpleOpen } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { GeoServer } from '@sirocco/shared';
import { servers as serversApi } from '../../api/endpoints';
import { BrandScreen } from '../../components/layout/Screens';
import { Button } from '../../components/ui/Button';
import { SearchField } from '../../components/ui/Field';
import { useAsync, useDebounced } from '../../hooks/useAsync';
import { useSession } from '../../store/SessionContext';
import { SiroccoMark } from '../../theme/illustrations';
import { illustrationDeJour, illustrationDeNuit } from '../../theme/illustrations';
import { t } from '../../i18n';

/**
 * « Choisis ton serveur ».
 *
 * L'ecran tient entier : titre, recherche, liste, ville choisie. Seule la
 * liste du milieu defile, entre la recherche et le bloc du bas — rien ne
 * descend, on ne quitte jamais l'ecran des yeux.
 *
 * Le mot « spot » ne s'affiche plus nulle part : c'est « serveur » que
 * lit le membre. Il reste le vocabulaire interne — noms de classes, de
 * fichiers et de routes — parce que le renommer ne change rien pour
 * personne et casserait des chemins ecrits partout.
 */
export function ChooseSpotScreen() {
  const { joinServer, user, ambiance } = useSession();

  /*
    Les cartes suivent la lumiere du moment : de nuit, ce sont les
    illustrations nocturnes.

    Seule celle du spot courant le faisait, pour n'avoir a charger que
    l'image deja en tete de l'ecran. Mais une rangee de cartes postales
    ensoleillees au milieu d'un ecran de nuit ne dit pas la meme ville
    que celle ou l'on entre : on choisissait Paris de jour pour arriver
    dans Paris de nuit. Les images nocturnes pesent le meme poids que
    les autres et se chargent paresseusement, comme elles.

    Une ville sans illustration nocturne garde la sienne de jour : le
    repli est dans `illustrationDeNuit`.
  */
  const illustrationDe = (serverId: string) =>
    ambiance === 'night' ? illustrationDeNuit(serverId) : illustrationDeJour(serverId);

  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const debounced = useDebounced(search, 200);
  const [selected, setSelected] = useState<GeoServer | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const { data: results } = useAsync(() => serversApi.list(debounced), [debounced]);
  /*
    Le catalogue entier, lu une fois. Il ne suit pas la recherche : la
    liste au repos reste la meme pendant qu'on tape, et effacer sa saisie
    la retrouve telle quelle plutot que de la faire clignoter.
  */
  const { data: catalogue } = useAsync(() => serversApi.list(''), []);

  const searching = search.trim().length > 0;

  /*
    La liste montree au milieu de l'ecran, dans l'ordre ou l'on veut la
    lire : d'abord les serveurs ouverts, du plus peuple au moins peuple,
    puis ceux a venir.

    Un serveur ouvert et vide passe donc devant un serveur annonce. Les
    deux affichent zero membre, mais l'un se rejoint aujourd'hui et
    l'autre non : c'est ce qui les separe, pas leur effectif.

    Le meme ordre s'applique a une recherche : la regle vaut pour la
    liste, pas pour l'un de ses etats.
  */
  /*
    Seules les villes ouvertes.

    Les autres etaient annoncees pour dire ou l'application allait, et
    l'on pouvait s'inscrire a leur ouverture. Mais un ecran qui s'appelle
    « Choisis ton serveur » et qui propose cinq portes fermees sur dix
    fait surtout perdre du temps a qui veut entrer quelque part
    aujourd'hui.

    Le tri par disponibilite ne sert donc plus a departager que des
    egaux : il reste, il ne coute rien, et il redeviendra utile le jour ou
    l'on remontrera des villes a venir.
  */
  const shown = [...(searching ? (results ?? []) : (catalogue ?? []))]
    .filter((server) => server.available)
    .sort((a, b) => Number(b.available) - Number(a.available) || b.memberCount - a.memberCount);

  /*
    Deux familles : les serveurs publics, ou tout le monde entre, et les
    serveurs prives — une ecole, une entreprise — que tout le monde voit
    mais ou l'on n'entre qu'avec son nom sur la liste.

    Les prives restent visibles : savoir qu'ils existent est ce qui donne
    envie d'en etre, et c'est a l'entree qu'on se voit refuser, pas en
    cherchant. La cle se lit une ligne par serveur prive, la sienne.
  */
  const publics = shown.filter((server) => !server.prive);
  const prives = shown.filter((server) => server.prive);
  const idsPrives = (catalogue ?? []).filter((server) => server.prive).map((server) => server.id);
  const { data: cles } = useAsync(
    () => serversApi.accesPrives(idsPrives),
    [idsPrives.join()],
  );
  /* Tant que la cle n'est pas lue, on n'affirme rien : ni ouvert, ni ferme. */
  const sansCle = (server: GeoServer) =>
    Boolean(server.prive) && cles != null && !cles.has(server.id);
  const membre = (server: GeoServer) =>
    Boolean(server.prive) && cles != null && cles.has(server.id);
  const ferme = selected !== null && sansCle(selected);

  async function onJoin() {
    if (!selected || ferme) return;
    setError(null);
    setPending(true);
    try {
      await joinServer(selected.id);
      navigate('/app/feed', { replace: true });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t.auth.spot.impossible);
    } finally {
      setPending(false);
    }
  }

  const familles = [
    { titre: t.auth.spot.publics, liste: publics },
    { titre: t.auth.spot.prives, liste: prives },
  ].filter(({ liste }) => liste.length > 0);

  /*
    Les deux lumieres de chaque carte sont allees chercher d'avance.

    Basculer le telephone du jour a la nuit change la source de chaque
    image. WebKit ne reclamait la nouvelle qu'au geste suivant : la carte
    gardait sa lumiere d'avant, puis changeait des qu'on effleurait
    l'ecran — et entre les deux, le temps du chargement, elle virait au
    gris. Une image deja dans le cache se pose sans rien demander.

    Ce sont des fichiers de l'application sur un telephone : les lire ne
    coute pas de reseau.
  */
  const clesDesCartes = shown.map((server) => server.id).join();
  useEffect(() => {
    for (const server of shown) {
      for (const source of [illustrationDeJour(server.id), illustrationDeNuit(server.id)]) {
        if (source) {
          const image = new Image();
          image.src = source;
        }
      }
    }
    // Les illustrations d'un serveur ne changent pas : la liste suffit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clesDesCartes]);

  const carte = (server: GeoServer) => (
    <li key={server.id}>
      <button
        className={`spot-result${selected?.id === server.id ? ' spot-result--on' : ''}`}
        aria-pressed={selected?.id === server.id}
        onClick={() => {
          /*
            Rappuyer sur la ville deja choisie la relache. C'est
            la seule facon de revenir sur son choix depuis que le
            bloc du bas a disparu : la selection ne se voit plus
            que dans la liste, elle doit donc s'y defaire aussi.
          */
          setSelected((precedent) => (precedent?.id === server.id ? null : server));
          setError(null);
        }}
      >
        {/*
          L'illustration de la ville, en fond de sa carte.

          Une liste de dix lignes blanches ne disait rien d'un
          endroit : on choisissait un nom, pas une ville. Le
          dessin est celui de l'en-tete du spot — le meme qu'on
          retrouve en entrant — donc la carte annonce ce a quoi
          on arrive.

          Les villes annoncees et pas encore ouvertes n'en ont
          pas : elles gardent leur carte unie plutot qu'un cadre
          vide. Le verre par-dessus, lui, est toujours la : c'est
          lui qui tient l'ecriture lisible sur un ciel clair
          comme sur une facade sombre.
        */}
        {illustrationDe(server.id) && (
          <img
            className="spot-result__fond"
            src={illustrationDe(server.id) ?? ''}
            alt=""
            /*
              Rien de paresseux ici : WebKit ne jugeait de ce qui est
              visible qu'au premier geste, et l'ecran s'ouvrait sur des
              cartes vides que toucher n'importe ou faisait paraitre.
              Elles sont six, elles sont dans l'application, et c'est
              l'ecran ou le dessin est le contenu.
            */
            loading="eager"
            /*
              Le decodage synchrone evite l'image d'avant figee a l'ecran
              le temps que la nouvelle soit prete : au changement de
              lumiere, la carte se repeint d'un coup ou pas du tout.
            */
            decoding="sync"
          />
        )}
        <span className="spot-result__verre" aria-hidden="true" />

        {/* Plus de drapeau : le pays est ecrit juste en dessous,
            et l'illustration dit deja de quelle ville il s'agit. */}
        <span className="spot-result__text">
          <span className="spot-result__name">
            {server.name}
            {!server.available && <em className="spot-result__soon">{t.auth.spot.bientot}</em>}
          </span>
          {/* Un serveur qui n'a pas ouvert n'a personne : afficher
              « 0 membre » ne dirait rien que « Bientôt » ne dise
              deja, juste au-dessus. */}
          <span className="spot-result__meta">
            {/* Sous le nom plutot qu'a cote : un nom d'ecole est long. */}
            {/*
                Le meme jeton dit les deux etats : rouge et cadenas ferme
                pour qui n'a pas la cle, vert et cadenas ouvert pour qui
                l'a. Tant que la cle n'est pas lue, c'est le rouge qui
                s'affiche — un serveur prive l'est de toute facon.
            */}
            {server.prive &&
              (membre(server) ? (
                <em
                  className="spot-result__prive spot-result__prive--membre"
                  title={t.auth.spot.tuEsMembre}
                >
                  <LockSimpleOpen size={13} weight="bold" aria-hidden="true" />
                  {/* Le cadenas ouvert ne dit rien a un lecteur d'ecran. */}
                  <span className="spot-result__lecture">{t.auth.spot.membre}</span>
                </em>
              ) : (
                <em className="spot-result__prive">
                  <LockSimple size={11} weight="bold" aria-hidden="true" />
                  {t.auth.spot.badgePrive}
                </em>
              ))}
            {/* Deux faits, deux elements : la ligne est deja une rangee
                espacee, elle n'a pas besoin d'un point pour les separer. */}
            {sansCle(server) ? (
              t.auth.spot.reserve
            ) : server.available ? (
              <>
                <span>{server.country}</span>
                <span>{t.auth.spot.membres(server.memberCount)}</span>
              </>
            ) : (
              server.country
            )}
          </span>
        </span>
      </button>
    </li>
  );

  return (
    <BrandScreen>
      <div className="spot">
        {/*
          Le retour vit en haut a gauche, la ou on le cherche, et non en
          bas sous le bouton principal : « Annuler » y avait l'air d'une
          seconde action a peser, alors qu'il ne fait que refermer. Il
          n'apparait que pour qui a deja un serveur — sans cela, il n'y a
          nulle part ou revenir.
        */}
        {user?.currentServerId && (
          <button className="spot__back" aria-label={t.commun.retour} onClick={() => navigate(-1)}>
            <CaretLeft className="sens-x" size={22} weight="bold" />
          </button>
        )}

        <div className="spot__logo">
          <SiroccoMark size={34} />
        </div>

        {/* Ni titre ni sous-titre : la marque est au-dessus, la
            recherche demande une ville, et la liste en montre six. Ce
            qu'on fait ici se voit sans qu'on l'ecrive. */}
        <SearchField
          tone="brand"
          placeholder={t.auth.spot.rechercher}
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setError(null);
          }}
          onClear={() => {
            setSearch('');
            setError(null);
          }}
          aria-label={t.auth.spot.rechercherLabel}
        />

        {/*
          La liste vit dans son propre creux : elle defile la, entre la
          recherche et le bloc de selection, et l'ecran ne bouge pas d'un
          pixel. Les villes du bas s'effacent en approchant du bord —
          c'est ce qui dit qu'il y en a d'autres en dessous.
        */}
        <div className="spot__stage">
          <ul className="spot__results">
            {familles.map(({ titre, liste }) => [
              /* Le titre n'a de sens que s'il y a deux familles a separer. */
              familles.length > 1 && (
                <li key={titre} className="spot__section">
                  {titre}
                </li>
              ),
              ...liste.map(carte),
            ])}

            {searching && shown.length === 0 && (
              <li className="spot__none">{t.auth.spot.aucun(search)}</li>
            )}
          </ul>
        </div>

        {/*
          Plus de bloc rappelant la ville choisie : elle se voit dans la
          liste, la ou on l'a designee. Le repeter en bas doublait
          l'information et poussait la liste vers le haut.
        */}
        <div className="spot__footer">
          {error && <p className="spot__error">{error}</p>}
          {/* Choisir un serveur prive sans en etre : on le dit, sans ouvrir. */}
          {ferme && !error && (
            <p className="spot__note">
              {t.auth.spot.prive(selected.name)}
            </p>
          )}

          <Button
            variant="brand"
            className="spot__cta"
            disabled={!selected || ferme}
            loading={pending}
            onClick={onJoin}
          >
            {ferme ? t.auth.spot.accesReserve : t.auth.spot.rejoindre}
          </Button>
        </div>
      </div>
    </BrandScreen>
  );
}
